import { useMemo } from 'react';
import { State } from '@weave/schema-gen-ts/dist/schemas/platform-location-feature/v1/platform-enums/location_feature_enum.pb';
import { Feature } from '@weave/schema-gen-ts/dist/shared/feature/location_feature.pb';
import { FeatureFlagStrategy } from '../types';
import { useAppFlagStore } from './use-feature-flag';

/**
 * `TRUELARK_AI_MESSAGING` for bridgeline AI Messaging and `WEAVE_MESSAGING_AIR` for steelthread AI Messaging
 */
export const MESSAGING_AIR_CUSTOMIZATION_FEATURE_ENUMS = [
  Feature.TRUELARK_AI_MESSAGING,
  Feature.WEAVE_MESSAGING_AIR,
] as const;

const DEFAULT_ACTIVE_STATES = [State.ACTIVE];

const evaluateIsActive = (values: boolean[], strategy: FeatureFlagStrategy): boolean => {
  if (values.length === 0) return false;
  return strategy === 'ALL' ? values.every(Boolean) : values.some(Boolean);
};

/**
 * Resolves `TRUELARK_AI_MESSAGING` and `WEAVE_MESSAGING_AIR` for the provided locations.
 * Returns combined and per-flag activity states and location maps.
 *
 * `strategy` defaults to `'ANY'` and controls whether one or every location must
 * be active. `activeStates` defaults to `[State.ACTIVE, State.PROMOTE]`.
 */
export const useMessagingAIRCustomizationFlag = (
  locationIds: string[],
  activeStates: State[] = DEFAULT_ACTIVE_STATES,
  strategy: FeatureFlagStrategy = 'ANY'
) => {
  const { getCustomizationFlagLocationMap } = useAppFlagStore();

  return useMemo(() => {
    const truelarkAIRawMap = getCustomizationFlagLocationMap({
      locationIds,
      featureEnum: Feature.TRUELARK_AI_MESSAGING,
    });
    const weaveAIRawMap = getCustomizationFlagLocationMap({
      locationIds,
      featureEnum: Feature.WEAVE_MESSAGING_AIR,
    });

    const truelarkMessagingAIFlagValueMap: Record<string, boolean> = {};
    const weaveMessagingAIFlagValueMap: Record<string, boolean> = {};
    const locationFlagValueMap: Record<string, boolean> = {};

    for (const locationId of locationIds) {
      const truelarkMessagingAIData = truelarkAIRawMap[locationId];
      const weaveMessagingAIData = weaveAIRawMap[locationId];

      const isTruelarkMessagingAIActive = truelarkMessagingAIData
        ? activeStates.includes(truelarkMessagingAIData.state ?? State.HIDE)
        : false;
      const isWeaveMessagingAIActive = weaveMessagingAIData
        ? activeStates.includes(weaveMessagingAIData.state ?? State.HIDE)
        : false;

      if (truelarkMessagingAIData) {
        truelarkMessagingAIFlagValueMap[locationId] = isTruelarkMessagingAIActive;
      }
      if (weaveMessagingAIData) {
        weaveMessagingAIFlagValueMap[locationId] = isWeaveMessagingAIActive;
      }

      // If at least one flag returned data for this location, record combined state
      if (truelarkMessagingAIData || weaveMessagingAIData) {
        locationFlagValueMap[locationId] = isTruelarkMessagingAIActive || isWeaveMessagingAIActive;
      }
    }

    const truelarkMessagingAICFValues = Object.values(truelarkMessagingAIFlagValueMap);
    const weaveMessagingAICFValues = Object.values(weaveMessagingAIFlagValueMap);
    const combinedMessagingAICFValues = Object.values(locationFlagValueMap);

    const truelarkMessagingAIActive = evaluateIsActive(truelarkMessagingAICFValues, strategy);
    const weaveMessagingAIActive = evaluateIsActive(weaveMessagingAICFValues, strategy);
    const isActive = evaluateIsActive(combinedMessagingAICFValues, strategy);

    return {
      isActive,
      locationFlagValueMap,
      truelarkMessagingAIActive,
      weaveMessagingAIActive,
      truelarkMessagingAIFlagValueMap,
      weaveMessagingAIFlagValueMap,
    };
  }, [activeStates, getCustomizationFlagLocationMap, locationIds, strategy]);
};
